export interface NumericTextNumberToken {
  type: "number";
  raw: string;
  value: number;
  format: NumberFlowProps["format"];
}

export interface NumericTextLiteralToken {
  type: "text";
  value: string;
}

export type NumericTextToken = NumericTextNumberToken | NumericTextLiteralToken;

const NUMERIC_PATTERN = /[+\-\u2212]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?/g;

export function tokenizeNumericText(text: string): NumericTextToken[] {
  const tokens: NumericTextToken[] = [];
  let cursor = 0;

  for (const match of text.matchAll(NUMERIC_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ type: "text", value: text.slice(cursor, index) });
    }
    const raw = match[0];
    const normalized = raw.replaceAll(",", "").replace("\u2212", "-");
    const value = Number(normalized);
    if (!Number.isFinite(value)) {
      tokens.push({ type: "text", value: raw });
    } else {
      tokens.push({
        type: "number",
        raw,
        value,
        format: formatForToken(raw),
      });
    }
    cursor = index + raw.length;
  }

  if (cursor < text.length) {
    tokens.push({ type: "text", value: text.slice(cursor) });
  }
  return tokens.length ? tokens : [{ type: "text", value: text }];
}

function formatForToken(raw: string): NumberFlowProps["format"] {
  const unsigned = raw.replace(/^[+\-\u2212]/, "");
  const [integer, fraction = ""] = unsigned.replaceAll(",", "").split(".");
  const preserveLeadingZeroes = integer.length > 1 && integer.startsWith("0");
  return {
    useGrouping: raw.includes(","),
    minimumIntegerDigits: preserveLeadingZeroes
      ? Math.min(integer.length, 21)
      : 1,
    minimumFractionDigits: fraction.length,
    maximumFractionDigits: fraction.length,
    signDisplay: raw.startsWith("+") ? "always" : "auto",
  };
}
import type { NumberFlowProps } from "@number-flow/react";
